import { getFeedbackToneVars } from './tones';

/**
 * stylesForms.js — 미팅지도 모달 폼·피드백·킵·계약 스타일
 *
 * 포함 스타일:
 *   - 결과 모달 주요/보조 액션 버튼
 *   - 모달 내 인풋·구분선·섹션
 *   - 피드백 옵션 그리드 버튼
 *   - 고정 액션 행 (미뤄짐/미팅인계)
 *   - 킵 목록 관련 (킵 버튼·목록·항목·해제·숨기기·토글)
 *   - 계약완료 토글·상세 입력·상품 chip
 */

// ── 결과 모달 액션 버튼 ────────────────────────────────────────────────────────

/** 저장 CTA (진한 블루) */
export const primaryActionStyle = {
  border: 'none',
  borderRadius: 8,
  background: '#1d4ed8',
  color: '#fff',
  fontSize: 13, fontWeight: 600,
  padding: '10px 20px', minHeight: 40,
  cursor: 'pointer',
  letterSpacing: '0.01em',
};

/** 취소·보조 버튼 (흰 배경 outline) */
export const ghostActionStyle = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  fontSize: 13, fontWeight: 600,
  padding: '10px 20px', minHeight: 40,
  cursor: 'pointer',
};

// ── 모달 내 인풋 필드 ──────────────────────────────────────────────────────────

/** 모달 필드 그리드 래퍼 */
export const modalFieldWrapStyle = { display: 'grid', gap: 12 };

/** 모달 텍스트 인풋 */
export const modalInputStyle = {
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '9px 12px',
  fontSize: 13, color: 'var(--text)',
  background: 'var(--bg-card)',
  width: '100%', boxSizing: 'border-box',
};

/** 모달 내 수평 구분선 */
export const modalDividerStyle = {
  height: 1, background: 'var(--bg-subtle)', margin: '6px 0',
};

/** 모달 내 섹션 래퍼 (피드백 그리드 등) */
export const modalSectionStyle = { display: 'grid', gap: 8 };

/** 섹션 레이블: small caps 회색 */
export const modalSectionLabelStyle = {
  fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

// ── 피드백 옵션 ────────────────────────────────────────────────────────────────

/** 피드백 옵션 3열 그리드 */
export const feedbackGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
  gap: 6,
};

/**
 * 피드백 옵션 버튼: 선택 시 solid dark, 기본 white outline
 * disabled=true (☆ 킵 활성) 시 회색 처리
 * @param {boolean} selected  선택 여부
 * @param {boolean} disabled  비활성 여부
 */
export const feedbackOptionBtnStyle = (selected, disabled = false, colorKey = 'blue') => {
  const tone = getFeedbackToneVars(colorKey);
  return {
    border: `1px solid ${selected ? tone.solidBorder : tone.border}`,
    borderRadius: 8,
    padding: '8px 6px',
    minHeight: 36,
    fontSize: 12,
    fontWeight: selected ? 700 : 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: disabled ? 'var(--bg-subtle)' : selected ? tone.solidBg : tone.bg,
    color: disabled ? 'var(--text-muted)' : selected ? tone.solidText : tone.text,
    boxShadow: selected ? `0 0 0 1px ${tone.solidBorder}` : 'none',
    transition: 'all 0.1s',
    textAlign: 'center',
    opacity: disabled ? 0.6 : 1,
    lineHeight: 1.25,
  };
};

// ── 고정 액션 행 (미뤄짐 / 미팅인계) ─────────────────────────────────────────

/** 고정 액션 버튼 행 (미뤄짐·미팅인계 두 버튼 나란히) */
export const fixedActionRowStyle = {
  display: 'flex',
  gap: 6,
  marginTop: 2,
};

/** 피드백 메모 textarea (미뤄짐/미팅인계 버튼 바로 아래, 2줄 고정) */
export const feedbackMemoStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 12,
  color: 'var(--text-sub)',
  resize: 'none',
  lineHeight: 1.5,
  marginTop: 6,
  outline: 'none',
  fontFamily: 'inherit',
};

/**
 * 고정 액션 버튼: 선택 시 solid dark, 기본 outline
 * @param {boolean} selected 선택 여부
 */
export const fixedActionBtnStyle = (selected) => ({
  flex: 1,
  border: `1px solid ${selected ? '#2563eb' : 'var(--border)'}`,
  borderRadius: 8,
  padding: '7px 0',
  fontSize: 12, fontWeight: 600,
  cursor: 'pointer',
  background: selected ? '#2563eb' : 'var(--bg-card)',
  color: selected ? '#fff' : 'var(--text-muted)',
  transition: 'all 0.1s',
});

// ── 킵 관련 ────────────────────────────────────────────────────────────────────

/**
 * 킵목록에추가 버튼: 활성(킵됨) 시 amber 강조
 * @param {boolean} active 킵 활성 여부
 */
export const keepBtnStyle = (active) => ({
  width: '100%',
  border: `1px solid ${active ? 'var(--mm-tone-amber-solid-border)' : 'var(--mm-tone-amber-border)'}`,
  borderRadius: 8,
  padding: '9px 0',
  fontSize: 12, fontWeight: 600,
  cursor: 'pointer',
  background: active ? 'var(--mm-tone-amber-solid-bg)' : 'var(--mm-tone-amber-bg)',
  color: active ? 'var(--mm-tone-amber-solid-text)' : 'var(--mm-tone-amber-text)',
  transition: 'all 0.1s',
});

/** 헤더 킵목록 버튼 */
export const keepListBtnStyle = {
  border: '1px solid var(--mm-tone-amber-border)',
  borderRadius: 8,
  padding: '4px 10px',
  fontSize: 12, fontWeight: 700,
  cursor: 'pointer',
  background: 'var(--mm-tone-amber-bg)',
  color: 'var(--mm-tone-amber-text)',
};

/** 킵목록 모달 아이템 카드 */
export const keepItemStyle = {
  background: 'var(--mm-tone-amber-bg)',
  border: '1px solid var(--mm-tone-amber-border)',
  borderLeft: '3px solid var(--mm-tone-amber-solid-bg)',
  borderRadius: 10,
  padding: '10px 12px',
};

/** 킵 아이템 상단: 회사명 + 배지 */
export const keepItemTopStyle = {
  display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6,
};

/** 킵 아이템 하단: 해제/숨기기 버튼 행 */
export const keepItemBottomStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
};

/** 킵 해제 버튼 */
export const keepRemoveBtnStyle = {
  border: '1px solid #fca5a5',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11, fontWeight: 600,
  cursor: 'pointer',
  background: 'var(--bg-card)',
  color: '#dc2626',
  flexShrink: 0,
};

/**
 * 마케터 킵 목록: 숨기기/표시 버튼
 * @param {boolean} isHidden 숨김 여부
 */
export const keepHideBtnStyle = (isHidden) => ({
  border: `1px solid ${isHidden ? 'var(--mm-tone-green-border)' : 'var(--border)'}`,
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11, fontWeight: 600,
  cursor: 'pointer',
  background: isHidden ? 'var(--mm-tone-green-bg)' : 'var(--bg-subtle)',
  color: isHidden ? 'var(--mm-tone-green-text)' : 'var(--text-muted)',
  flexShrink: 0,
});

/**
 * 마케터 킵 목록: 전체보기/숨긴것제외 토글 버튼
 * @param {boolean} isShowAll 전체 표시 여부
 */
export const keepViewToggleBtnStyle = (isShowAll) => ({
  border: `1px solid ${isShowAll ? 'var(--mm-tone-blue-border)' : 'var(--border)'}`,
  borderRadius: 6,
  padding: '4px 12px',
  fontSize: 11, fontWeight: 700,
  cursor: 'pointer',
  background: isShowAll ? 'var(--mm-tone-blue-bg)' : 'var(--bg-subtle)',
  color: isShowAll ? 'var(--mm-tone-blue-text)' : 'var(--text-muted)',
});

/** 킵 뱃지 chip */
export const keepChipStyle = {
  display: 'inline-block',
  background: 'var(--mm-tone-amber-bg)',
  border: '1px solid var(--mm-tone-amber-border)',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--mm-tone-amber-text)',
};

// ── 계약완료 ───────────────────────────────────────────────────────────────────

/**
 * 계약완료 토글 버튼: 활성 solid green / 비활성 neutral outline
 * @param {boolean} active 활성 여부
 */
export const contractToggleBtnStyle = (active) => ({
  width: '100%',
  border: `1px solid ${active ? 'var(--mm-tone-green-solid-border)' : 'var(--border)'}`,
  borderRadius: 8,
  padding: '11px 0',
  fontSize: 14, fontWeight: 600,
  cursor: 'pointer',
  background: active ? 'var(--mm-tone-green-solid-bg)' : 'var(--bg-card)',
  color: active ? 'var(--mm-tone-green-solid-text)' : 'var(--text-muted)',
  letterSpacing: '0.01em',
  transition: 'all 0.15s',
});

/** 계약 상세 입력 박스 (확장 시): 좌측 green accent 바로 구분 */
export const contractExpandedStyle = {
  display: 'grid',
  gap: 10,
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderLeft: '3px solid #16a34a',
  borderRadius: 8,
  padding: '12px 14px',
};

/** 상품 quick-select chip 래퍼 */
export const contractProductChipsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
};

/**
 * 상품 quick-select chip 버튼: 선택 solid dark-navy / 기본 outline
 * @param {boolean} selected 선택 여부
 */
export const contractProductChipStyle = (selected) => ({
  border: `1px solid ${selected ? 'var(--mm-tone-blue-solid-border)' : 'var(--border)'}`,
  borderRadius: 6,
  padding: '5px 12px',
  fontSize: 12, fontWeight: selected ? 600 : 500,
  cursor: 'pointer',
  background: selected ? 'var(--mm-tone-blue-solid-bg)' : 'var(--bg-card)',
  color: selected ? 'var(--mm-tone-blue-solid-text)' : 'var(--text-sub)',
  transition: 'all 0.1s',
});
